/**
 * Filters questions from a starting point until it finds a branching question
 * @param questions - The full array of observation questions
 * @param startIndex - Optional index to start filtering from
 * @returns Array of questions up until (but not including) the next branching question
 */
export const filterQuestionsUpToBranching = (
	questions: ObservationQuestion[],
	startIndex?: number
) => {
	// Default to start of array if no startIndex provided
	const start = startIndex ?? 0;

	const filteredQuestions: ObservationQuestion[] = [];

	for (let i = start; i < questions.length; i++) {
		const currentQuestion = questions[i];

		if (currentQuestion.branchType === "END_OF_FORM") {
			filteredQuestions.push(currentQuestion);
			break;
		}

		if (currentQuestion.branchType === "NEXT") {
			filteredQuestions.push(currentQuestion);
		} else {
			// We are branched here

			if (
				currentQuestion.branches &&
				Object.keys(currentQuestion.branches).length > 0
			) {
				filteredQuestions.push(currentQuestion);

				// if we have previously answered this question, lets continue
				if (!currentQuestion.givenAnswer) {
					break;
				} else {
					const answerBranch =
						currentQuestion.branches[currentQuestion.givenAnswer];

					if (answerBranch === "END_OF_FORM") {
						break;
					} else if (answerBranch !== "NEXT") {
						const branchQuestionisAlreadyVisible = filteredQuestions.find(
							(q) => q.uuid === answerBranch
						);

						if (!branchQuestionisAlreadyVisible) {
							const goToQuestionIndex = questions.findIndex(
								(q) => q.uuid === answerBranch
							);

							if (goToQuestionIndex === -1) {
								break;
							}

							// We want to go to the question before the go_to question
							// because i++ will add +1 at start of loop
							i = goToQuestionIndex - 1;
						}
					}
				}
			}

			if (currentQuestion.goTo) {
				filteredQuestions.push(currentQuestion);

				const goToQuestionisAlreadyVisible = filteredQuestions.find(
					(q) => q.uuid === currentQuestion.goTo
				);

				if (!goToQuestionisAlreadyVisible) {
					// Question is not added to filtered
					const goToQuestionIndex = questions.findIndex(
						(q) => q.uuid === currentQuestion.goTo
					);

					if (goToQuestionIndex === -1) {
						break;
					}

					// We want to go to the question before the go_to question
					// because i++ will add +1 at start of loop
					i = goToQuestionIndex - 1;
				}
			}
		}
	}

	return filteredQuestions;
};

/**
 * Handles when a branching question directs to a specific question by UUID
 * This can jump forward or backward in the form
 * @param questions - The full array of observation questions
 * @param visibleQuestions - Currently visible questions in the form
 * @param answeredQuestion - The question that was just answered
 * @param nextQuestionId - The UUID of the next question to show
 * @returns Updated array of visible questions
 */
export const handleSpecificQuestion = (
	questions: ObservationQuestion[],
	visibleQuestions: ObservationQuestion[],
	answeredQuestion: ObservationQuestion,
	nextQuestionId: string
): ObservationQuestion[] => {
	// If we're directed to the current question, no changes needed
	if (answeredQuestion.uuid === nextQuestionId) {
		return visibleQuestions;
	}

	// Find positions of both the next question and answered question in current visible questions
	const nextQuestionIndex = visibleQuestions.findIndex(
		(q) => q.uuid === nextQuestionId
	);

	const answeredQuestionIndex = visibleQuestions.findIndex(
		(q) => q.uuid === answeredQuestion.uuid
	);

	// If we're jumping backwards in the form, trim all questions after the current answer
	// This prevents duplicate questions from appearing
	if (nextQuestionIndex !== -1 && nextQuestionIndex < answeredQuestionIndex) {
		return visibleQuestions.slice(0, answeredQuestionIndex + 1);
	}

	// Find the target question in the full question set
	const nextQuestion = questions.find((q) => q.uuid === nextQuestionId);

	// If target question doesn't exist, maintain current state
	if (!nextQuestion) {
		return visibleQuestions;
	}

	if (nextQuestion.branchType === "END_OF_FORM") {
		return [...visibleQuestions, nextQuestion];
	}

	// Only add additional questions if the target question has a goTo property
	if (nextQuestion.goTo) {
		const gotToQuestion = questions.find((q) => q.uuid === nextQuestion.goTo);

		if (!gotToQuestion) {
			throw new Error("Broken state, we don't recognize the given answer");
		}

		// question is already visible so lets do nothing more
		if (visibleQuestions.find((q) => q.uuid === gotToQuestion.uuid)) {
			return [...visibleQuestions, nextQuestion];
		}

		if (gotToQuestion.uuid === nextQuestion.uuid) {
			return [...visibleQuestions, nextQuestion];
		}

		// from a branching question
		const questionsUpUntilNextBranch = filterQuestionsUpToBranching(
			questions,
			gotToQuestion.order - 1 // we want to include the gotoquestion here because we have not added it yet
		);
		return [...visibleQuestions, nextQuestion, ...questionsUpUntilNextBranch];
	} else {
		// from a branching question
		const questionsUpUntilNextBranch = filterQuestionsUpToBranching(
			questions,
			nextQuestion.order - 1 // we search from the question after nextQuestion here
		);
		return [...visibleQuestions, ...questionsUpUntilNextBranch];
	}
};

/**
 * Main handler for when a branching question is answered
 * Determines which questions should be visible based on the answer given
 * @param answeredQuestion - The question that was just answered
 * @param questions - The full array of observation questions
 * @param visibleQuestions - Currently visible questions in the form
 * @returns Updated array of visible questions based on the answer's branching logic
 */
export const handleBranchedQuestionAnswered = (
	answeredQuestion: ObservationQuestion,
	questions: ObservationQuestion[],
	visibleQuestions: ObservationQuestion[]
): ObservationQuestion[] => {
	// We don't really track answers here but we need to, so lets make a side not here
	const questionIndexInQuestions = questions.findIndex(
		(q) => q.uuid === answeredQuestion.uuid
	);

	if (questionIndexInQuestions !== -1) {
		questions[questionIndexInQuestions].givenAnswer =
			answeredQuestion.givenAnswer;
	}

	// Find where we are in the current visible questions
	const questionIndex = visibleQuestions.findIndex(
		(q) => q.uuid === answeredQuestion.uuid
	);

	// Remove any questions that were shown after this one
	// This handles cases where someone changes their answer and we need to
	// remove the previously shown branched questions
	const updatedQuestions = visibleQuestions.slice(0, questionIndex + 1);

	if (answeredQuestion.branchType === "END_OF_FORM") {
		return updatedQuestions;
	}

	if (answeredQuestion.branchType === "NEXT") {
		const nextQuestion = questions.find(
			(q) => q.order === answeredQuestion.order + 1
		);

		if (nextQuestion)
			return handleSpecificQuestion(
				questions,
				updatedQuestions,
				answeredQuestion,
				nextQuestion?.uuid
			);

		return updatedQuestions;
	}

	if (answeredQuestion.branchType === "MULTIPLE") {
		let nextQuestionAction: string | null | undefined;

		if (answeredQuestion.branches) {
			const givenAnswer = answeredQuestion.givenAnswer;

			// This could be NEXT, END_OF_FORM or a specific question UUID
			nextQuestionAction = answeredQuestion.branches?.[givenAnswer];

			if (!nextQuestionAction) {
				throw new Error("Broken state, we don't recognize the given answer");
			}
		} else {
			// assume its a go_to question

			// this IS either null or a question Id
			nextQuestionAction = answeredQuestion.goTo as string | null | undefined;

			if (!nextQuestionAction) {
				throw new Error("Broken state, we are expecting a go_to but its null");
			}
		}

		// Handle the three possible branch types:
		// 1. "NEXT" - Go to the next sequential question
		if (nextQuestionAction === "NEXT") {
			const nextQuestion = questions.find(
				(q) => q.order === answeredQuestion.order + 1
			);

			if (nextQuestion)
				return handleSpecificQuestion(
					questions,
					updatedQuestions,
					answeredQuestion,
					nextQuestion?.uuid
				);

			return updatedQuestions;
		}

		// 2. "END_OF_FORM" - Stop showing any more questions
		if (nextQuestionAction === "END_OF_FORM") {
			return updatedQuestions;
		}

		return handleSpecificQuestion(
			questions,
			updatedQuestions,
			answeredQuestion,
			nextQuestionAction
		);
	}

	// crash and burn
	throw new Error("Branch type not supported");
};
